import { useState } from 'react';
import React from 'react';

// @ts-ignore-next-line no types
import matrix from 'calendar-matrix';
import {
  navigateMonthsLocal,
  startOfMonthLocal,
  startOfDayLocal,
  dateToIsoLocal,
  isSameDayLocal,
  isoToDateLocal,
  getLocaleDateStringLocal,
} from '../../../../shared/lib/time';

export interface CalendarDateRange {
  from?: Date;
  to?: Date;
}

export interface CalendarDayContext {
  dateIso: string;
  isDisabled: boolean;
  isSelectedDate: boolean;
  isHighlighted: boolean;
  isRangeStart: boolean;
  isRangeEnd: boolean;
  isInRange: boolean;
  isOutOfBounds: boolean;
  isCurrentDate: boolean;
}

function getDayContext(
  dayMatrixDay: number,
  monthDate: Date,
  selectedDate?: Date,
  selectedRange?: CalendarDateRange,
  enabledDates?: string[] //iso dates
): CalendarDayContext {
  let date: Date;

  if (dayMatrixDay < 0 && dayMatrixDay > -15) {
    date = navigateMonthsLocal(monthDate, 1, Math.abs(dayMatrixDay));
  } else if (dayMatrixDay <= -15) {
    date = navigateMonthsLocal(monthDate, -1, Math.abs(dayMatrixDay));
  } else {
    date = navigateMonthsLocal(monthDate, 0, dayMatrixDay);
  }

  const dateIso = dateToIsoLocal(date);

  const isRangeStart = !!selectedRange?.from && isSameDayLocal(date, selectedRange.from);
  const isRangeEnd = !!selectedRange?.to && isSameDayLocal(date, selectedRange.to);
  const isInRange =
    !!selectedRange?.from &&
    !!selectedRange?.to &&
    date > selectedRange.from &&
    date < selectedRange.to;
  const isSelectedDate = !!selectedDate && isSameDayLocal(date, selectedDate);
  const isHighlighted = isSelectedDate || isRangeStart || isRangeEnd || isInRange;
  const existsInDateList = (enabledDates ?? []).some(d => dateIso === d);

  return {
    dateIso,
    isSelectedDate,
    isHighlighted,
    isRangeStart,
    isRangeEnd,
    isInRange,
    isOutOfBounds: dayMatrixDay < 0,
    isDisabled: enabledDates ? !existsInDateList : false,
    isCurrentDate: isSameDayLocal(date, startOfDayLocal()),
  };
}

export interface CalendarComponents<T> {
  className?: string;
  titleClassName?: string;
  daysClassName?: string;
  NavButton: React.ComponentType<{ direction: 'prev' | 'next'; onClick: () => void }>;
  DayButton: React.ComponentType<{
    day: number;
    idx: number;
    dayContext: CalendarDayContext;
    onClick: () => void;
  }>;
  Badge?: React.ComponentType<{ color: T; idx: number }>;
  TodayButton?: React.ComponentType<{ onClick: () => void }>;
  ClearButton?: React.ComponentType<{ onClick: () => void }>;
}

export interface CalendarProps<T> {
  responsive?: boolean;
  enabledDates?: string[];
  selectedDate?: Date;
  selectedRange?: CalendarDateRange;
  readOnly?: boolean;
  showClear?: boolean;
  showToday?: boolean;
  onClear?: () => void;
  onDateSelect?: (date: Date) => void;
  onRangeSelect?: (range: CalendarDateRange) => void;
  weekDayRender?: (weekDay: string, idx: number) => React.ReactNode;
  addMarkers?: (dayContext: CalendarDayContext) => T[];
  subtitle?: () => React.ReactNode;
}

export function createCalendar<T = string>(components: CalendarComponents<T>) {
  const {
    NavButton,
    DayButton,
    Badge,
    TodayButton,
    ClearButton,
    className: factoryClasses,
    titleClassName: factoryTitleClasses,
    daysClassName: factoryDaysClasses,
  } = components;

  return function Calendar(props: CalendarProps<T> & React.HTMLAttributes<HTMLDivElement>) {
    const {
      responsive,
      selectedDate: propSelectedDate,
      selectedRange: propSelectedRange,
      enabledDates,
      readOnly,
      showClear,
      showToday,
      onClear,
      onDateSelect,
      onRangeSelect,
      weekDayRender,
      addMarkers,
      className,
      subtitle,
      ...rest
    } = props;

    const isRangeMode = !!onRangeSelect;

    const selectedDate = propSelectedDate ? startOfDayLocal(propSelectedDate) : undefined;
    const selectedRange: CalendarDateRange | undefined = propSelectedRange
      ? {
          from: propSelectedRange.from ? startOfDayLocal(propSelectedRange.from) : undefined,
          to: propSelectedRange.to ? startOfDayLocal(propSelectedRange.to) : undefined,
        }
      : undefined;

    const [monthDate, setMonthDate] = useState(
      startOfMonthLocal(selectedDate || startOfDayLocal())
    );
    const [awaitingTo, setAwaitingTo] = useState(false);

    function handleDateSelect(date: Date) {
      if (readOnly) return;

      if (!isRangeMode) {
        onDateSelect?.(date);
        return;
      }

      const from = selectedRange?.from;

      if (!awaitingTo || !from || date < from) {
        onRangeSelect?.({ from: date, to: undefined });
        setAwaitingTo(true);
      } else {
        onRangeSelect?.({ from, to: date });
        setAwaitingTo(false);
      }
    }

    function handleTodayClick() {
      const today = startOfDayLocal();
      setMonthDate(startOfMonthLocal(today));
      if (!isRangeMode) {
        onDateSelect?.(today);
      } else {
        onRangeSelect?.({ from: today, to: today });
        setAwaitingTo(false);
      }
    }

    function handleClearClick() {
      if (!isRangeMode) {
        onDateSelect?.(undefined as any);
      } else {
        onRangeSelect?.({ from: undefined, to: undefined });
        setAwaitingTo(false);
      }
      onClear?.();
    }

    const dayMatrix: number[][] = matrix(monthDate);
    const weekDays = Array.from({ length: 7 }, (_, i) =>
      getLocaleDateStringLocal(new Date(0, 0, i), { weekday: 'short' })
    );

    const classes = ['grid-cols-7 gap-4 p-4 items-center justify-center'];
    classes.push(responsive ? 'grid' : 'inline-grid');
    if (factoryClasses) classes.push(factoryClasses);
    if (className) classes.push(className);

    const titleClasses = [''];
    if (factoryTitleClasses) titleClasses.push(factoryTitleClasses);

    const dayClasses = ['text-center'];
    if (factoryDaysClasses) dayClasses.push(factoryDaysClasses);

    return (
      <div {...rest} className={classes.join(' ')}>
        <div className="col-span-7 flex items-center justify-between mb-2">
          <NavButton
            direction="prev"
            onClick={() => setMonthDate(navigateMonthsLocal(monthDate, -1))}
          />
          <div className={titleClasses.join(' ')}>
            {getLocaleDateStringLocal(monthDate, { month: 'long', year: 'numeric' })}
          </div>
          <NavButton
            direction="next"
            onClick={() => setMonthDate(navigateMonthsLocal(monthDate, 1))}
          />
        </div>

        {weekDays.map((wd, i) =>
          weekDayRender ? (
            weekDayRender(wd, i)
          ) : (
            <div key={wd} className={dayClasses.join(' ')}>
              {wd}
            </div>
          )
        )}

        {dayMatrix.flat().map((d, i) => {
          const dayContext = getDayContext(d, monthDate, selectedDate, selectedRange, enabledDates);
          const markers = addMarkers?.(dayContext) ?? [];
          const isHighlighted =
            dayContext.isSelectedDate ||
            dayContext.isRangeStart ||
            dayContext.isRangeEnd ||
            dayContext.isInRange;

          return (
            <div
              key={d}
              className="relative"
              style={{ width: '35px', height: '35px', justifySelf: 'center', alignSelf: 'center' }}
            >
              <DayButton
                day={d}
                idx={i}
                dayContext={dayContext}
                onClick={() => handleDateSelect(isoToDateLocal(dayContext.dateIso))}
              />
              {!isHighlighted && Badge && markers.length > 0 && (
                <div
                  className="absolute w-full flex items-center justify-center space-x-1 pointer-events-none"
                  style={{ bottom: '-5px' }}
                >
                  {markers.map((color, idx) => (
                    <Badge key={idx} color={color} idx={idx} />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {(showToday || showClear) && (
          <div className="col-span-7 flex items-center gap-2">
            {showToday && TodayButton && <TodayButton onClick={handleTodayClick} />}
            <div className="ml-auto" />
            {showClear && ClearButton && <ClearButton onClick={handleClearClick} />}
          </div>
        )}
        {subtitle && <div className="col-span-7">{subtitle()}</div>}
      </div>
    );
  };
}
